import moment from 'moment-timezone';

const logging = process.env.NODE_ENV === 'development';

export const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

export const renderRoute = (route: string, vars: Record<string, string>) => {
  return route.replace(/:(\w+)/g, (match, p1) => vars[p1] || match);
};

export const formatDate = (dateString: string): string => {
  if (!dateString) return 'NA';
  return moment(dateString).format('MMM D, YYYY');
};

// For calendar-only values (bookingDate, dob, slot startDate/endDate) -
// these are serialized by the backend as a full ISO UTC timestamp (e.g.
// "2026-11-09T00:00:00.000Z") even though only the date part is
// meaningful. formatDate() above converts that instant to the browser's
// local timezone before formatting, which shifts the displayed calendar
// date back a day for any viewer behind UTC (e.g. America/Toronto) -
// confirmed as the cause of bookings showing a day earlier than stored.
// Reading the date in UTC (matching how it was written) avoids that shift.
export const formatDateOnly = (dateString: string): string => {
  if (!dateString) return 'NA';
  return moment.utc(dateString).format('MMM D, YYYY');
};
// Used across 7 pages (bookings, contact forms, content, newsletter,
// notification logs, SEO) to display every createdAt/updatedAt timestamp
// shown to an admin - so this assumption, if wrong, is a widespread,
// silent time-display bug, not a one-off.
//
// ASSUMPTION (undocumented until now - Admin Low #18, AUDIT_FIX_LOG.md):
// `dateString` arrives as a naive "YYYY-MM-DD HH:mm:ss" string with NO
// timezone/offset info, and this function's first .tz() call treats that
// naive string *as if* it were already Asia/Kolkata local time, before
// converting to America/Toronto (the business's actual timezone) for
// display. That only produces a correct result if whatever produced
// `dateString` was actually running in Kolkata local time when it
// serialized the timestamp (e.g. a server/dev machine set to IST) - not
// because the data itself is meaningfully "Kolkata-origin" in any other
// sense.
//
// NOT changed here: the backend (clear-scan) now serializes timestamps
// via a documented `utcToToronto()` UTC-aware helper (see that repo's
// AUDIT_FIX_LOG.md), which suggests this Kolkata assumption may already
// be stale for API-sourced data - but confirming that needs checking an
// actual API response's raw timestamp format against a live environment,
// which is out of scope for this pass (no live env available - see this
// repo's own database-policy notes). Changing this without that
// confirmation risks silently shifting every displayed time by the
// Kolkata/Toronto offset (~9.5-10.5 hours depending on DST) for data
// that's actually already correct. Flagging for verification, not
// guessing.
export const formatDateTime = (dateString: string): string => {
  return moment
    .tz(dateString, 'YYYY-MM-DD HH:mm:ss', 'Asia/Kolkata')
    .tz('America/Toronto')
    .format('MMM D, YYYY, hh:mm A');
};

export const formatTime = (timeString: string): string => {
  if (!timeString) return 'NA';
  return moment(timeString, 'HH:mm:ss').format('hh:mm A');
};
export const currentDateTime = () => {
  return moment().utc().toISOString();
};
